import { IBalusterGeometry, IHandrailGeometry, IRailingConfig, IRailingGeometry, IStairConfig } from '@stair-craft/common/util';

import { balusterCount } from '../shared-baluster-count/baluster-count';
import { openingRectOf, placeUpperRailingsOnSlab } from '../shared-upper-railings-on-slab/upper-railings-on-slab';

/**
 * Railings around the upper-floor opening for a straight stair, anchored in WORLD
 * coordinates (not the stair group) so they stay put when the stair is repositioned.
 * Guards the back and side edges of the opening; the front (stair exit) is left open.
 */
export function generateUpperFloorRailing(config: IStairConfig): IRailingGeometry[] {
    const rc: IRailingConfig = config.railing;
    const y: number = config.floorHeight + config.floorThickness;
    const railings: IRailingGeometry[] = [];

    // Upper railing is anchored to the OPENING in WORLD coordinates so it stays put when the
    // stair group is repositioned (e.g., when angle changes shift the stair entry).
    const ox: number = config.openingX;
    const oz: number = config.openingZ;
    const ow: number = config.openingWidth;
    const ol: number = config.openingLength;
    const rw: number = config.roomWidth;

    const edges: [number, number, number, number][] = [];

    if (ox > 0) {
        edges.push([ox, oz, ox, oz + ol]);
    }
    if (ox + ow < rw) {
        edges.push([ox + ow, oz, ox + ow, oz + ol]);
    }
    if (oz > 0) {
        edges.push([ox, oz, ox + ow, oz]);
    }
    // Front edge (stair exit) — never block with railing

    for (const [x1, z1, x2, z2] of edges) {
        const edgeLength: number = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);
        if (edgeLength < 100) {
            continue;
        }

        // Posts at both ends of the edge
        const posts: IBalusterGeometry[] = [
            { position: { y, x: x1, z: z1 }, height: rc.height, rotation: 0 },
            { position: { y, x: x2, z: z2 }, height: rc.height, rotation: 0 },
        ];

        // Balusters along the edge
        const slots: number = balusterCount(edgeLength, rc);
        const balusters: IBalusterGeometry[] = [];
        for (let i: number = 1; i < slots; i++) {
            const t: number = i / slots;
            balusters.push({
                position: {
                    y,
                    x: x1 + (x2 - x1) * t,
                    z: z1 + (z2 - z1) * t,
                },
                height: rc.height,
                rotation: 0,
            });
        }

        // Handrail along the edge
        const handrail: IHandrailGeometry = {
            points: [
                { x: x1, y: y + rc.height, z: z1 },
                { x: x2, y: y + rc.height, z: z2 },
            ],
            width: rc.handrailWidth,
            height: rc.handrailHeight,
        };

        const verticalSide: 'left' | 'right' = x1 <= config.openingX + config.openingWidth / 2 ? 'left' : 'right';
        const side: 'left' | 'right' = x1 === x2 ? verticalSide : 'left';

        railings.push({
            side,
            balusters,
            handrail,
            posts,
            kind: 'upper',
            balusterWidth: rc.balusterWidth,
            postWidth: rc.postWidth,
        });
    }

    placeUpperRailingsOnSlab(railings, openingRectOf(config), config.railing.balusterSpacing, {
        width: config.roomWidth,
        length: config.roomLength,
    });
    return railings;
}
